import { useCallback, useEffect, useState } from 'react';
import { styled } from 'styled-components';

import { apiGetShortUrl } from '#/api/shortUrl/apiShortUrl';
import type { ApiShortUrlType } from '#/api/shortUrl/types/ApiShortUrlType';
import { Icon } from '#/components/Icon/Icon';
import { useCopyToClipboard } from '#/hooks/useCopyToClipboard';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors, fontWeights } from '#/theme/theme';

import { StyledShareButton } from './ShareStyleButton';
import { makeParameters } from './helpers/makeParameters';
import { withSocialUtm } from './helpers/withSocialUtm';

const BUTTON_SIZE = 48;
const MESSAGE_OFFSET = 28;
const POPUP_MARGIN = 100;

const CopiedMessage = styled.div`
  align-items: center;
  background-color: ${colors.brandBlue};
  border-radius: 8px;
  color: ${colors.white};
  display: flex;
  font-size: 16px;
  font-weight: ${fontWeights.semiBold};
  gap: 8px;
  left: ${MESSAGE_OFFSET}px;
  padding: 4px 8px;
  position: absolute;
  top: ${MESSAGE_OFFSET}px;
  width: max-content;

  &.left {
    left: auto;
    right: ${MESSAGE_OFFSET}px;
  }
`;

const Container = styled.div`
  display: flex;
  position: relative;
  height: ${BUTTON_SIZE}px;
  width: ${BUTTON_SIZE}px;
`;

type Props = {
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  object: Record<string, any>;
  objectType: ApiShortUrlType;
  utmParams: Record<string, string>;
  qaId?: string;
};

export function SocialCopyToClipboardButton({
  object,
  objectType,
  utmParams,
  qaId,
}: Props) {
  const { i18n, locale } = useI18n();
  const { copyToClipboard, showCopiedMessage } = useCopyToClipboard({
    copiedMessageTimeoutMs: 'default',
  });

  const [shortUrl, setShortUrl] = useState<string>();
  const [showLeft, setShowLeft] = useState(false);

  const handleClick = useCallback(() => {
    if (!shortUrl) return;

    void copyToClipboard(shortUrl);
  }, [shortUrl, copyToClipboard]);

  useEffect(() => {
    void apiGetShortUrl({
      toType: objectType,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      toId: object.id,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      toUrl: object.url ? object.url[locale] : null,
      extraParams: makeParameters(withSocialUtm(utmParams, 'copy')),
      locale,
    }).then((url) => setShortUrl(url));
  }, [locale, objectType, object.id, object.url, utmParams]);

  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  const ref = useCallback((node: any) => {
    if (
      node &&
      window &&
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      node.getBoundingClientRect().right + POPUP_MARGIN > window.innerWidth
    ) {
      setShowLeft(true);
    } else {
      setShowLeft(false);
    }
  }, []);

  if (!shortUrl) return null;

  return (
    <Container ref={ref}>
      {showCopiedMessage && (
        <CopiedMessage className={showLeft ? 'left' : ''}>
          <Icon name="simple-check" colorName="white" size={16} />
          {i18n('Link copied')}
        </CopiedMessage>
      )}

      <StyledShareButton
        title={i18n('Copy Link')}
        aria-label={i18n('Copy Link')}
        onClick={handleClick}
        data-url={shortUrl}
        data-qa-id={qaId}
      >
        <Icon name="link" colorName="white" size={25} />
      </StyledShareButton>
    </Container>
  );
}
